import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import { CategoryService } from '@/lib/api/services/category'
import { Tag } from '@/types/tag'
import { CategoryItem, Categorization, CategoriesState } from '@/types/category'
import { deduplicateRequest } from '@/utility/requestDeduplication'

// カテゴリリスト（大カテゴリはここで保持）
const categoryList: CategoryItem[] = [
  { id: 1, name: 'キャリアパス', order: 2 },
  { id: 2, name: 'カルチャー', order: 7 },
  { id: 3, name: '制度', order: 6 },
  { id: 4, name: '創業・起業', order: 8 },
  { id: 5, name: '事業・経営', order: 5 },
  { id: 8, name: '社会', order: 9 },
  { id: 9, name: 'その他', order: 10 },
  { id: 10, name: '職種', order: 1 },
  { id: 11, name: '働き方', order: 4 },
  { id: 12, name: '仕事への思い', order: 3 },
]

// 初期状態
const initialState: CategoriesState = {
  allTags: [],
  selectTag: null,
  categorization: [],
  selectedFilterCategories: [],
  articleCategoryFilterPreCheck: [],
  categoryList,
  loading: false,
  error: null,
}

// 非同期アクション
export const getAllTags = createAsyncThunk<Tag[], void>(
  'categories/getAllTags',
  async (_, { rejectWithValue }) => {
    try {
      // Use request deduplication to prevent concurrent identical requests
      const allTags = await deduplicateRequest('categories:getAllTags', async () => {
        return await CategoryService.getAllTags()
      })
      return allTags
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'タグの取得に失敗しました')
    }
  },
)

export const getSelectedTag = createAsyncThunk<Tag, string>(
  'categories/getSelectedTag',
  async (slug: string, { rejectWithValue }) => {
    try {
      throw new Error('getSelectedTag は未実装です')
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : '選択されたタグの取得に失敗しました',
      )
    }
  },
)

// カテゴリ分類を作成
const createCategorization = (allTags: Tag[], categoryList: CategoryItem[]): Categorization[] => {
  const newCategory: Categorization[] = []
  const orderedCategories = [...categoryList].sort((a, b) => a.order - b.order)

  orderedCategories.forEach((category) => {
    const sameCategory = allTags
      .filter((tag) => tag.categoryId === category.id)
      .map((tag) => ({ ...tag, isSelected: false }))

    if (sameCategory.length > 0) {
      const categoryItem: Categorization = {
        categoryName: category.name,
        records: sameCategory,
      }
      newCategory.push(categoryItem)
    }
  })

  return newCategory
}

const categoriesSlice = createSlice({
  name: 'categories',
  initialState,
  reducers: {
    setSelectTag: (state, action: PayloadAction<Tag>) => {
      state.selectTag = action.payload
    },

    setCategorization: (state) => {
      state.categorization = createCategorization(state.allTags, state.categoryList)
    },

    setSelectedFilterCategory: (state, action: PayloadAction<Tag>) => {
      const category = action.payload
      const selectedIds = state.selectedFilterCategories.map((v) => v.id)
      const alreadySelected = selectedIds.includes(category.id)

      if (!alreadySelected) {
        state.selectedFilterCategories.push(category)
        // categorization内のisSelected状態を更新
        state.categorization.forEach((categoryGroup) => {
          categoryGroup.records.forEach((tag, index) => {
            if (tag.id === category.id) {
              categoryGroup.records[index] = {
                ...tag,
                isSelected: true,
              }
            }
          })
        })
      } else {
        // 選択から削除
        state.selectedFilterCategories = state.selectedFilterCategories.filter(
          (v) => v.id !== category.id,
        )
        // categorization内のisSelected状態を更新
        state.categorization.forEach((categoryGroup) => {
          categoryGroup.records.forEach((tag, index) => {
            if (tag.id === category.id) {
              categoryGroup.records[index] = {
                ...tag,
                isSelected: false,
              }
            }
          })
        })
      }
    },

    removeSelected: (state) => {
      state.selectedFilterCategories = []
      state.categorization.forEach((categoryGroup) => {
        categoryGroup.records.forEach((tag, index) => {
          categoryGroup.records[index] = {
            ...tag,
            isSelected: false,
          }
        })
      })
    },

    setArticleCategoryFilterPreCheck: (state, action: PayloadAction<Tag[]>) => {
      const current = action.payload
      state.articleCategoryFilterPreCheck = current
      state.selectedFilterCategories = []

      state.categorization.forEach((categoryGroup) => {
        categoryGroup.records.forEach((tag, index) => {
          categoryGroup.records[index] = {
            ...tag,
            isSelected: false,
          }

          current.forEach((preCheckedTag) => {
            if (tag.id === preCheckedTag.id) {
              categoryGroup.records[index] = {
                ...tag,
                isSelected: true,
              }
              state.selectedFilterCategories.push(preCheckedTag)
            }
          })
        })
      })
    },

    clearError: (state) => {
      state.error = null
    },
  },

  extraReducers: (builder) => {
    builder
      // getAllTags
      .addCase(getAllTags.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getAllTags.fulfilled, (state, action) => {
        state.loading = false
        state.allTags = action.payload
        state.categorization = createCategorization(action.payload, state.categoryList)
      })
      .addCase(getAllTags.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

      // getSelectedTag
      .addCase(getSelectedTag.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getSelectedTag.fulfilled, (state, action) => {
        state.loading = false
        state.selectTag = action.payload
      })
      .addCase(getSelectedTag.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
  },
})

export const {
  setSelectTag,
  setCategorization,
  setSelectedFilterCategory,
  removeSelected,
  setArticleCategoryFilterPreCheck,
  clearError,
} = categoriesSlice.actions

export default categoriesSlice.reducer
